/**
 * AppLayout: AppBar + persistent Drawer + Outlet. Rendered inside AuthGate, so
 * it only mounts when authenticated.
 *
 * Product-admin gate: an authenticated user without the `productAdminOf` JWT
 * claim sees ProductAdminRequired in-place — no redirect loop. The gate is
 * in-component by design; it is never a route loader.
 */
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import { useCallback, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { useCurrentProductAdmin, useAuth, type CurrentProductAdmin } from '@/auth/AuthContext';
import { authApi } from '@/api/auth';
import { clearDeepLink } from '@/auth/silentRefresh';
import { env } from '@/config/env';
import { BOTTOM_NAV_ITEM, NAV_GROUPS, type NavItem } from './nav';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LoadingSpinner } from './LoadingSpinner';
import { ProductAdminRequired } from './ProductAdminRequired';

const DRAWER_WIDTH = 246;
const CONTENT_MAX_WIDTH = 1480;

/** Two-letter initials from name, else the first letter of the email. */
function getInitials(productAdmin: CurrentProductAdmin): string {
  if (productAdmin.name) {
    const parts = productAdmin.name.trim().split(/\s+/);
    const first = parts[0];
    const last = parts[parts.length - 1];
    if (parts.length >= 2 && first && last) {
      return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
    }
    return (parts[0]?.[0] ?? '?').toUpperCase();
  }
  return (productAdmin.email[0] ?? '?').toUpperCase();
}

function capitalize(value: string): string {
  return value.length > 0 ? value[0]!.toUpperCase() + value.slice(1) : value;
}

export function AppLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { t } = useCopy();
  const productAdmin = useCurrentProductAdmin();
  const { status, signOut: ctxSignOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = useCallback(async () => {
    setMenuAnchor(null);
    try {
      await authApi.logout();
    } catch {
      /* logout errors are not blocking — proceed to the signed-out page */
    }
    clearDeepLink();
    ctxSignOut();
    navigate('/auth/signed-out', { replace: true });
  }, [ctxSignOut, navigate]);

  // Product-admin gate: render before drawing the shell.
  if (status === 'booting') return <LoadingSpinner />;
  if (status === 'authed' && !productAdmin) return <ProductAdminRequired />;

  const initials = productAdmin ? getInitials(productAdmin) : '?';

  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {NAV_GROUPS.map((group, idx) => (
        <Box key={group.groupKey} sx={{ mt: idx === 0 ? 0.5 : 1 }}>
          {/* Section heading lives outside <List> so the <ul> only contains <li> children. */}
          <Box sx={{ px: 2, pt: 1, pb: 0.25 }}>
            <Typography
              variant="caption"
              component="p"
              sx={{ letterSpacing: '0.08em', fontWeight: 600, color: 'text.secondary' }}
            >
              {t(group.groupKey, group.groupFallback)}
            </Typography>
          </Box>
          <List dense>
            {group.items.map((item) => (
              <NavLinkItem key={item.to} item={item} onClick={() => setMobileOpen(false)} t={t} />
            ))}
          </List>
          {idx < NAV_GROUPS.length - 1 ? <Divider sx={{ my: 1 }} /> : null}
        </Box>
      ))}
      <Box sx={{ mt: 'auto', pb: 1 }}>
        <Divider sx={{ mb: 1 }} />
        <List dense>
          <NavLinkItem item={BOTTOM_NAV_ITEM} onClick={() => setMobileOpen(false)} t={t} />
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          zIndex: (th) => th.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {!isDesktop ? (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t('common.menu', 'Menu')}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Typography
            variant="h1"
            component={NavLink}
            to="/"
            sx={{
              fontSize: '1.05rem',
              ml: isDesktop ? 0 : 1,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 600,
            }}
          >
            {t('app.shellTitle', 'Dink Product Admin · Platform Console')}
          </Typography>
          {/* Environment banner. */}
          <Chip label={capitalize(env.deployEnv)} size="small" variant="outlined" sx={{ ml: 2 }} />
          {/* Scope label — this console is platform-scoped, not per-tenant. */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2, color: 'text.secondary' }}
          >
            <BusinessIcon fontSize="small" aria-hidden="true" />
            <Typography variant="body2">{t('shell.scope', 'Platform — all tenants')}</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <LanguageSwitcher />
          <Tooltip title={t('shell.notificationsAriaLabel', 'Notifications')}>
            <IconButton
              onClick={() => navigate('/notifications')}
              aria-label={t('shell.notificationsAriaLabel', 'Notifications')}
              sx={{ mr: 1 }}
            >
              <NotificationsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={productAdmin?.email ?? t('shell.accountMenu', 'Account')}>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label={t('shell.accountMenu', 'Account')}
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: 'primary.main',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {productAdmin ? (
              <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
                {productAdmin.name ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {productAdmin.name}
                  </Typography>
                ) : null}
                <Typography
                  variant="body2"
                  sx={{ color: productAdmin.name ? 'text.secondary' : 'text.primary' }}
                >
                  {productAdmin.email}
                </Typography>
              </Box>
            ) : null}
            <Divider />
            <MenuItem
              onClick={() => {
                void handleSignOut();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('nav.signOut', 'Sign out')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: 0 },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Container
          maxWidth={false}
          sx={{ py: 4, maxWidth: CONTENT_MAX_WIDTH }}
          key={location.pathname.split('/')[1] ?? 'root'}
        >
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}

interface NavLinkItemProps {
  item: NavItem;
  onClick: () => void;
  t: (key: string, fallback?: string) => string;
}

function NavLinkItem({ item, onClick, t }: NavLinkItemProps) {
  // ListItem renders as <li>, satisfying the <ul> → <li> HTML requirement.
  // ListItemButton renders as <a> when component={NavLink} — it must live
  // inside the <li>, not directly in the <ul>.
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={NavLink}
        to={item.to}
        onClick={onClick}
        sx={{
          '&.active': {
            bgcolor: 'action.selected',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={t(item.key, item.fallback)} />
      </ListItemButton>
    </ListItem>
  );
}
